import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { credentialsApi, type CredentialDetail } from '@/api/credentials';
import InfoTooltip from '@/components/InfoTooltip';
import { useAuthStore } from '@/stores/auth';
import { confirmDialog } from '@/stores/dialog';

// ============================================================
// BreakGlassToggleSection — flag pentru recuperare urgență (owner only)
// ============================================================

export function BreakGlassToggleSection({ cred }: { cred: CredentialDetail }) {
  const { t } = useTranslation();
  const currentUserId = useAuthStore((s) => s.user?.id);
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => credentialsApi.setBreakGlass(cred.id, !cred.is_break_glass),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credential', cred.id] });
      queryClient.invalidateQueries({
        queryKey: ['credentials'],
        predicate: (q) => q.queryKey[0] === 'credentials',
      });
      queryClient.invalidateQueries({ queryKey: ['break-glass'] });
    },
  });

  if (cred.owner_id !== currentUserId) return null;

  return (
    <section
      className="rounded-lg p-3 flex items-start justify-between gap-3"
      style={{
        background: cred.is_break_glass ? 'var(--bg-rose)' : 'var(--bg-surface)',
        border: `1px solid ${cred.is_break_glass ? 'var(--c-rose)' : 'var(--border)'}`,
      }}
    >
      <div className="min-w-0">
        <div
          className="text-[12.5px] font-medium flex items-center gap-2"
          style={{ color: cred.is_break_glass ? 'var(--c-rose)' : 'var(--text)' }}
        >
          {t('nav.break_glass')}
          <InfoTooltip
            text={t('break_glass.info_tooltip')}
            color={cred.is_break_glass ? 'var(--c-rose)' : 'var(--text-muted)'}
            width={300}
          />
          {cred.is_break_glass && (
            <span
              className="font-mono text-[9px] px-1.5 py-0.5 rounded"
              style={{
                background: 'var(--c-rose)',
                color: 'var(--bg-elevated)',
                letterSpacing: '0.1em',
              }}
            >
              {t('vault_ui.detail.bg_active')}
            </span>
          )}
        </div>
        <p
          className="text-[11px] mt-0.5"
          style={{ color: 'var(--text-muted)' }}
        >
          {cred.is_break_glass
            ? t('vault_ui.detail.bg_on_hint')
            : t('vault_ui.detail.bg_off_hint')}
        </p>
      </div>
      <button
        type="button"
        onClick={async () => {
          if (
            cred.is_break_glass ||
            await confirmDialog(
              t('vault_ui.detail.bg_confirm'),
            )
          ) {
            mutation.mutate();
          }
        }}
        className="btn-outline text-xs flex-shrink-0"
        style={
          cred.is_break_glass
            ? {
                color: 'var(--c-rose)',
                borderColor: 'var(--c-rose)',
              }
            : undefined
        }
        disabled={mutation.isPending}
      >
        {mutation.isPending
          ? '...'
          : cred.is_break_glass
            ? t('vault_ui.detail.bg_remove')
            : t('vault_ui.detail.bg_move')}
      </button>
    </section>
  );
}
